(ns ui.view.grid-opensearch.custom-renderers
  (:require [clojure.string :as string]
            [basic-components.inputs.footprint :refer [viewer]]
            [basic-components.inputs.wysiwyg]
            [common.localization :as loc]
            [frontend.routing :as routing]
            [frontend.utils :as fu]
            [oops.core :as oops]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [ui.components.highlight-text.view :as highlight-text]
            [ui.components.overflow-chips.view :as overflow-chips]
            [ui.view.grid-opensearch.utils :as grid-utils]))

(defn- normalize-id
  [value]
  (cond
    (number? value) value
    (string? value) (let [parsed (js/parseInt value 10)]
                      (when-not (js/isNaN parsed)
                        parsed))
    :else nil))

(defn- normalize-entity-type
  [value]
  (let [as-keyword (cond
                     (keyword? value)
                     value

                     (string? value)
                     (let [clean (string/replace value #"^:" "")]
                       (when (seq clean)
                         (keyword clean)))

                     :else
                     nil)]
    (when as-keyword
      (if (namespace as-keyword)
        as-keyword
        (keyword "entity.type" (name as-keyword))))))

(defn- render-open-button
  [props-js]
  (let [data (oops/oget props-js "data")
        context (oops/oget props-js "context")
        on-open-entity (some-> context (aget "onOpenEntity"))
        entity-id (or (some-> data (aget "original.db/id") normalize-id)
                      (some-> data (aget "db/id") normalize-id)
                      (some-> data (aget "id") normalize-id))
        entity-type (normalize-entity-type
                     (or (some-> data (aget "original.entity/type"))
                         (some-> data (aget "entity/type"))
                         (some-> context (aget "entityType"))))]
    [:div
     {:class "row-open-button-container opacity-0 absolute right-2 top-0 flex items-center h-full"}
     [:button
      {:class    "bg-base-light border rounded-md h-7 px-3 text-base-light-text shadow-[0_1px_2px_0_rgb(0_0_0_/_0.05)] hover:bg-base-light-text transition-colors"
       :on-click (fn [e]
                   (.preventDefault e)
                   (.stopPropagation e)
                   (cond
                     (fn? on-open-entity)
                     (on-open-entity (js->clj data :keywordize-keys true))

                     (some? entity-id)
                     (routing/open-entity {:db/id       entity-id
                                           :entity/type entity-type})))}
      (loc/tr :open)]]))

(defn- link-renderer
  [href-prefix]
  (fn [props-js]
    (let [props (js->clj props-js :keywordize-keys true)
          value (:value props)]
      (r/as-element
       (when (seq (str value))
         [:a {:href   (str href-prefix value)
              :target "_blank"
              :class  "text-blue-600 hover:underline"}
          (str value)])))))

(defn- url-renderer
  [props-js]
  (let [props (js->clj props-js :keywordize-keys true)
        value (:value props)
        href (some-> value str fu/sanitize-url)]
    (r/as-element
     (when (and href (seq (str value)))
       [:a {:href   href
            :target "_blank"
            :class  "text-blue-600 hover:underline"}
        (str value)]))))

(defn- boolean-renderer
  [props-js]
  (let [props (js->clj props-js :keywordize-keys true)
        value (:value props)]
    (r/as-element
     [:span {:class (if value
                      "text-emerald-600"
                      "text-slate-400")}
      (if value
        [:i.fal.fa-check-circle]
        [:i.fal.fa-times-circle])])))

(def multiline-class
  "The classes of the text of a multiline cell. The cell has no vertical padding: the row has a
   fixed height and the cell gives the line height, so a padding put the text 4 px lower than
   the text of the other cells of the row."
  "whitespace-pre-line")

(def ^:private one-line-clamp-style
  "The cell shows the first line of the text. An ellipsis shows that the text has
   more, and the title of the cell has all of it."
  {:display         "-webkit-box"
   :WebkitLineClamp 1
   :WebkitBoxOrient "vertical"
   :overflow        "hidden"})

(defn multiline-hiccup
  [value]
  [:div {:class multiline-class
         :style one-line-clamp-style
         :title (str value)}
   (str value)])

(defn- multiline-renderer
  [props-js]
  (let [props (js->clj props-js :keywordize-keys true)
        value (:value props)]
    (r/as-element
     (multiline-hiccup value))))

(defn- highlighted-cell-text
  "The text of a cell with the parts that the search of the grid found marked. The
   row can have the value of the column marked by OpenSearch (`__highlight`), for a
   typo in the title."
  [grid-id props-js]
  (let [words @(rf/subscribe [:ui.view.grid-opensearch.sub/query-words grid-id])
        flat-field (oops/oget props-js "?colDef.?context.?flat-field")
        marked (some-> (oops/oget props-js "?data.?__highlight")
                       (js->clj)
                       (get flat-field)
                       (first))
        text (or (oops/oget props-js "?valueFormatted")
                 (oops/oget props-js "?value"))]
    [highlight-text/view {:text   (when (some? text) (str text))
                          :words  words
                          :marked marked}]))

(defn- highlight-renderer
  "The cell renderer of a text column that marks the parts that the search found."
  [grid-id]
  (fn [props-js]
    (r/as-element
     [highlighted-cell-text grid-id props-js])))

(defn- highlight-multiline-renderer
  "A multiline cell as `multiline-hiccup` shows it, with the parts that the search
   found marked."
  [grid-id]
  (fn [props-js]
    (r/as-element
     [:div {:class multiline-class
            :style one-line-clamp-style
            :title (str (oops/oget props-js "?value"))}
      [highlighted-cell-text grid-id props-js]])))

(def ^:private highlighted-data-types
  "The data types of the cells that mark the parts that the search found: the texts
   and the labels that the search looks in."
  #{:data.type/text
    :data.type/text-simple
    :data.type/id
    :data.type/ref-single
    :data.type/keyword})

(defn- richtext-renderer
  [props-js]
  (let [props (js->clj props-js :keywordize-keys true)
        value (:value props)]
    (r/as-element
     [:div {:class "max-h-24 overflow-hidden py-1"}
      [viewer {:editor/type :editor.type/wysiwyg
               :value       value}]])))

(defn- structured-value-items
  [value]
  (let [decoded (if (string? value)
                  (or (grid-utils/parse-edn-string value) value)
                  value)]
    (cond
      (map? decoded)
      (->> decoded
           (take 4)
           (mapv (fn [[k v]]
                   {:label (cond
                             (keyword? k) (name k)
                             :else (str k))
                    :value (if (map? v)
                             (pr-str v)
                             (str v))})))

      (sequential? decoded)
      (->> decoded
           (take 4)
           (mapv (fn [item]
                   {:value (str item)})))

      (some? decoded)
      [{:value (str decoded)}]

      :else
      [])))

(defn- structured-data-renderer
  [props-js]
  (let [props (js->clj props-js :keywordize-keys true)
        value (:value props)
        items (structured-value-items value)]
    (r/as-element
     [:div {:class "py-1 text-xs leading-5"}
      (if (seq items)
        (into
         [:div {:class "flex flex-col gap-0.5"}]
         (map-indexed
          (fn [idx {:keys [label value]}]
            ^{:key (str "structured-item-" idx "-" label "-" value)}
            [:div {:class "truncate"}
             (if label
               [:<>
                [:span {:class "font-medium text-slate-600"} (str label ": ")]
                [:span {:class "text-slate-900"} value]]
               [:span {:class "text-slate-900"} value])])
          items))
        [:span {:class "text-slate-400"} ""])])))

(defn- tag-class
  []
  "inline-flex items-center rounded px-2 py-1 text-xs leading-none bg-emerald-100 text-emerald-700")

(defn- keyword-label
  [field-schema item]
  (or (some->> (:options field-schema)
               (seq)
               (fu/find-option-label item))
      (when (keyword? item)
        (name item))
      (some-> item str)))

(def ^:private overflow-badge-class
  "The class of the count of the chips that do not fit in the cell."
  "inline-flex items-center rounded px-2 py-1 text-xs leading-none bg-slate-100 text-slate-700")

(defn multi-value-title
  "The text of all the values of a cell with chips, for the title of the cell."
  [field-schema items]
  (->> items
       (map (fn [item]
              (if (= (:field/data-type field-schema) :data.type/keyword-multi)
                (keyword-label field-schema item)
                (str item))))
       (remove string/blank?)
       (string/join ", ")))

(defn- multi-item-element
  [field-schema item]
  (let [grid-editor-type (get-in field-schema [:field/default-visibility :grid])]
    (cond
      (= grid-editor-type :editor.type/url-multi)
      (let [href (some-> item str fu/sanitize-url)]
        (when href
          [:a {:href   href
               :target "_blank"
               :class  (str (tag-class) " hover:underline")}
           (str item)]))

      (= grid-editor-type :editor.type/email-multi)
      [:a {:href  (str "mailto:" item)
           :class (str (tag-class) " hover:underline")}
       (str item)]

      (= (:field/data-type field-schema) :data.type/keyword-multi)
      [:span {:class (tag-class)}
       (keyword-label field-schema item)]

      :else
      [:span {:class (tag-class)}
       (str item)])))

(defn- multi-value-renderer
  [field-schema]
  (fn [props-js]
    (let [props (js->clj props-js :keywordize-keys true)
          value (:value props)
          field-str (or (oops/oget+ props-js "colDef.context.field-str")
                        (oops/oget+ props-js "colDef.context.field-path-str")
                        (oops/oget+ props-js "colDef.field-str")
                        (oops/oget+ props-js "colDef.field-path-str"))
          raw-value (or value
                        (when field-str
                          (-> props-js
                              (oops/oget "data")
                              (aget field-str))))
          items (let [normalized-items (grid-utils/normalize-multi-values raw-value)]
                  (if (and (contains? #{:data.type/keyword-multi
                                        :data.type/ref-multi}
                                      (:field/data-type field-schema))
                           (= 1 (count normalized-items))
                           (string? (first normalized-items))
                           (string/includes? (first normalized-items) ";"))
                    (->> (string/split (first normalized-items) #";")
                         (map string/trim)
                         (remove string/blank?)
                         vec)
                    normalized-items))]
      ;; The row of a grid has a fixed height. The chips stay on one line, and a
      ;; count shows the values that do not fit.
      (r/as-element
       [overflow-chips/view
        {:chips       (into [] (keep (fn [item]
                                       (multi-item-element field-schema item)))
                            items)
         :title       (multi-value-title field-schema items)
         :badge-class overflow-badge-class}]))))

(defn get-field-cell-renderer
  "The cell renderer of a column of `field-schema`, or nil for the default cell. A
   text cell of the grid `grid-id` marks the parts that the search found."
  [field-schema {:keys [grid-id]}]
  (let [{:keys [field/data-type]} field-schema
        multi-data-type? (some #{data-type}
                               [:data.type/ref-multi
                                :data.type/keyword-multi
                                :data.type/text-multi
                                :data.type/url-multi])
        is-name-col? (= (:field/attribute field-schema) (:field/name-attribute field-schema))]
    (cond

      is-name-col?
      (fn [props-js]
        (let [props (js->clj props-js :keywordize-keys true)
              value (:value props)]
          (r/as-element
           [:<>
            (if grid-id
              [highlighted-cell-text grid-id props-js]
              value)
            [render-open-button props-js]])))

      multi-data-type?
      (multi-value-renderer field-schema)

      (= data-type :data.type/boolean)
      boolean-renderer

      (= data-type :data.type/email)
      (link-renderer "mailto:")

      (= data-type :data.type/text-url)
      url-renderer

      (= data-type :data.type/text-multiline)
      (if grid-id
        (highlight-multiline-renderer grid-id)
        multiline-renderer)

      (= data-type :data.type/text-richtext)
      richtext-renderer

      (some #{data-type}
            [:data.type/text-edn
             :data.type/financials
             :data.type/tesi-financials
             :data.type/tesi-financials-2])
      structured-data-renderer

      (and grid-id
           (contains? highlighted-data-types data-type))
      (highlight-renderer grid-id))))

(defn currency-cell-hiccup
  "The cell of a currency value. The label of the currency is on the left and the
   number is on the right, so that the numbers of the rows are below each other
   and the labels are below each other. In a column that is too narrow, the label
   is cut first. When the number does not fit alone, it ends with an ellipsis, as
   the text of another cell does, so that a cut number is not taken for a whole
   number. `parts` is `{:label .. :amount ..}`, from `grid-utils/currency-cell-parts`."
  [{:keys [label amount]}]
  [:div {:class "flex w-full items-center justify-between gap-2"}
   [:span {:class "min-w-0 shrink-[1000] truncate"} label]
   [:span {:class "min-w-0 truncate tabular-nums"} amount]])

(defn currency-cell-renderer
  "The AG Grid cell renderer of a currency value. It takes the same data as the
   value formatter of the column: `default-currency`, `default-multiplier`,
   `field-schema` and `currency-flat-field`. A cell with no value shows nothing."
  [{:keys [default-currency default-multiplier field-schema currency-flat-field]}]
  (fn [params-js]
    (r/as-element
     (when-let [parts (grid-utils/currency-cell-parts
                       {:default-currency    default-currency
                        :default-multiplier  default-multiplier
                        :field-schema        field-schema
                        :currency-flat-field currency-flat-field
                        :params-js           params-js
                        :value               (oops/oget+ params-js "value")})]
       (currency-cell-hiccup parts)))))
